import { useState, useEffect, useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { INTEGRATIONS, type IntegrationManifest, type IntegrationCategory } from '@dunea/shared';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetStaticDeploymentByChatId, type StaticDeployment } from '~/lib/api/deployments';
import { apiGetIntegrations, apiSaveIntegrationConfig, type IntegrationState } from '~/lib/api/integrations';
import { $integrationRequest } from '~/lib/stores/integrations';
import { classNames } from '~/utils/classNames';
import { PluginsSection } from './PluginsSection';

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  auth: 'Authentication',
  payments: 'Payments',
};

export function IntegrationsPanel() {
  const currentChatId = useStore(chatId);
  const [deployment, setDeployment] = useState<StaticDeployment | null>(null);
  const [states, setStates] = useState<IntegrationState[]>([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentChatId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const dep = await apiGetStaticDeploymentByChatId(currentChatId);
    setDeployment(dep);
    setStates(dep?.id ? await apiGetIntegrations(dep.id) : []);
    setLoading(false);
  }, [currentChatId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stateFor = (id: string) => states.find((s) => s.integrationId === id);

  // "Add" tells the agent to wire the integration (same as typing it in chat).
  // Enablement is NOT set here — it's recorded reliably from the app's source at
  // deploy time (when the library is actually used), not by a UI switch.
  const handleAdd = (m: IntegrationManifest) => {
    $integrationRequest.set(m.invokePrompt);
    toast.info(`Adding ${m.title}…`);
  };

  const byCategory = (cat: IntegrationCategory) => INTEGRATIONS.filter((i) => i.category === cat);
  const categories = (Object.keys(CATEGORY_LABELS) as IntegrationCategory[]).filter((c) => byCategory(c).length > 0);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-hairline">
        <div className="flex items-center gap-2">
          <div className="i-ph:plugs-connected text-base text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Integrations</h3>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="i-svg-spinners:90-ring-with-bg text-xl text-muted" />
          </div>
        ) : (
          <>
            {!deployment && (
              <p className="text-xs text-muted bg-raised border border-hairline rounded-lg p-3">
                Add integrations any time — they’re wired into your code now. Publish your app to configure custom
                credentials (e.g. your own Google branding).
              </p>
            )}

            {categories.map((cat) => (
              <div key={cat} className="space-y-2">
                <h4 className="text-xs font-medium text-muted uppercase tracking-wide">{CATEGORY_LABELS[cat]}</h4>
                {byCategory(cat).map((m) => {
                  const state = stateFor(m.id);
                  const enabled = Boolean(state?.enabled);

                  return (
                    <IntegrationTile
                      key={m.id}
                      manifest={m}
                      enabled={enabled}
                      hasCustomCredentials={Boolean(state?.hasCustomCredentials)}
                      deploymentId={deployment?.id ?? null}
                      expanded={managing === m.id}
                      onAdd={() => handleAdd(m)}
                      onToggleManage={() => setManaging(managing === m.id ? null : m.id)}
                      onSaved={load}
                    />
                  );
                })}
              </div>
            ))}

            {/* The app's plugins (shop, payments, reviews…): on/off and what's missing. */}
            {currentChatId && <PluginsSection appId={currentChatId} />}
          </>
        )}
      </div>
    </div>
  );
}

function IntegrationTile({
  manifest,
  enabled,
  hasCustomCredentials,
  deploymentId,
  expanded,
  onAdd,
  onToggleManage,
  onSaved,
}: {
  manifest: IntegrationManifest;
  enabled: boolean;
  hasCustomCredentials: boolean;
  deploymentId: string | null;
  expanded: boolean;
  onAdd: () => void;
  onToggleManage: () => void;
  onSaved: () => void;
}) {
  const canBYO = manifest.credentials.mode !== 'shared';

  return (
    <div className="bg-raised rounded-lg border border-hairline">
      <div className="flex items-start gap-3 p-3">
        <div className={classNames(manifest.icon, 'text-xl text-[#D9F26A] mt-0.5')} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-primary">{manifest.title}</span>
            {enabled ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-green-400 flex items-center gap-1">
                  <span className="i-ph:check-circle" />
                  {hasCustomCredentials ? 'Custom' : 'Added'}
                </span>
                {canBYO && (
                  <button
                    onClick={onToggleManage}
                    className="text-xs px-2 py-1 rounded-md border border-hairline text-secondary hover:text-primary hover:bg-sunken transition-colors"
                  >
                    Manage
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={onAdd}
                className="text-xs px-2.5 py-1 rounded-md bg-[#D9F26A] text-black font-medium hover:opacity-90 transition-opacity"
              >
                Add
              </button>
            )}
          </div>
          <p className="text-xs text-muted mt-0.5">{manifest.description}</p>
        </div>
      </div>

      {expanded && canBYO && <ManageCredentials manifest={manifest} deploymentId={deploymentId} onSaved={onSaved} />}
    </div>
  );
}

function ManageCredentials({
  manifest,
  deploymentId,
  onSaved,
}: {
  manifest: IntegrationManifest;
  deploymentId: string | null;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!deploymentId) {
      toast.error('Publish your app first to store custom credentials.');
      return;
    }

    setSaving(true);

    try {
      await apiSaveIntegrationConfig(deploymentId, manifest.id, { enabled: true, config: values });
      toast.success(`${manifest.title} credentials saved.`);
      onSaved();
    } catch (e) {
      console.error(e);
      toast.error('Could not save your credentials. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-hairline p-3 space-y-3">
      <p className="text-xs text-muted">
        Use your own {manifest.title} app to show your brand. Otherwise Dunea’s shared sign-in is used.
      </p>

      {manifest.credentials.redirectUri && (
        <div className="text-xs">
          <span className="text-muted">Authorized redirect URI</span>
          <button
            onClick={() => {
              navigator.clipboard.writeText(manifest.credentials.redirectUri!);
              toast.info('Redirect URI copied');
            }}
            className="mt-1 w-full text-left font-mono text-[11px] bg-sunken border border-hairline rounded px-2 py-1.5 text-secondary hover:text-primary truncate"
            title="Click to copy"
          >
            {manifest.credentials.redirectUri}
          </button>
        </div>
      )}

      {manifest.credentials.fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <label className="text-xs text-secondary">{f.label}</label>
          <input
            type={f.secret ? 'password' : 'text'}
            placeholder={f.placeholder}
            value={values[f.key] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            className="w-full text-xs bg-sunken border border-hairline rounded px-2 py-1.5 text-primary placeholder:text-muted focus:outline-none focus:border-[#D9F26A]/50"
          />
          {f.help && <p className="text-[10px] text-muted">{f.help}</p>}
        </div>
      ))}

      <button
        onClick={save}
        disabled={saving}
        className="text-xs px-3 py-1.5 rounded-md bg-[#D9F26A] text-black font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
      >
        {saving ? 'Saving…' : 'Save credentials'}
      </button>
    </div>
  );
}
